// =============================================================================
// VandieZ admin-web -- KYC review per-user detail (/kyc/users/:userId)
// =============================================================================
// Renders the user's identity + business profile + every non-deleted KYC
// document. Each document gets a card with:
//   * Inline preview (image rendered from a blob URL fetched via the
//     auth-gated /kyc/documents/{id}/file endpoint).
//   * OCR-extracted data side-by-side with the image.
//   * Status pill + three action buttons (Approve / Reject / Resubmit).
//
// Reject + Resubmit expand a notes textarea that's required before submit
// (mirrors backend validation: rejected/resubmit_requested without notes
// returns 422 DECISION_NOTES_REQUIRED).
//
// After a decision lands, the doc's card refreshes inline. When the
// decision flips the user to document_verified (last required doc
// approved), a banner appears at the top.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Loader2,
  RefreshCw,
  XCircle,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { kycAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  AdminReviewStatus,
  ApiVerificationStatus,
  KycAdminDecision,
  KycAdminDocument,
  KycAdminUserDetail,
} from "@/types/kyc";

function formatDateTime(iso: string | null): string {
  if (iso === null) {return "—";}
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function formatBytes(n: number): string {
  if (n < 1024) {return `${n} B`;}
  if (n < 1024 * 1024) {return `${(n / 1024).toFixed(1)} KB`;}
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const ADMIN_STATUS_STYLES: Record<AdminReviewStatus, string> = {
  pending: "bg-warning-subtle text-warning-subtle-foreground",
  approved: "bg-success-subtle text-success-subtle-foreground",
  rejected: "bg-danger-subtle text-danger-subtle-foreground",
  resubmit_requested: "bg-primary-subtle text-primary-subtle-foreground",
};

const ADMIN_STATUS_LABEL: Record<AdminReviewStatus, string> = {
  pending: "Pending review",
  approved: "Approved",
  rejected: "Rejected",
  resubmit_requested: "Resubmit requested",
};

const API_STATUS_LABEL: Record<ApiVerificationStatus, string> = {
  not_attempted: "Not attempted",
  pending: "API pending",
  verified: "API verified",
  rejected: "API rejected",
};

function StatusPill({ status }: { status: AdminReviewStatus }): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        ADMIN_STATUS_STYLES[status],
      )}
    >
      {ADMIN_STATUS_LABEL[status]}
    </span>
  );
}

interface DocumentCardProps {
  doc: KycAdminDocument;
  userId: string;
  onDecisionApplied: (
    docId: string,
    newStatus: AdminReviewStatus,
    notes: string | null,
    userVerifiedNow: boolean,
  ) => void;
}

function DocumentCard({
  doc,
  userId,
  onDecisionApplied,
}: DocumentCardProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [pendingDecision, setPendingDecision] =
    useState<KycAdminDecision | null>(null);
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch + revoke the document blob URL.
  useEffect(() => {
    if (accessToken === null) {return;}
    const controller = new AbortController();
    // Track liveness + the URL via a single mutable cell so the cleanup
    // closure shares state with the in-flight fetch. The fetch writes
    // `revokedRef.url`, the cleanup flips `revokedRef.revoked` to true
    // and revokes the URL if it was minted.
    const revokedRef: { revoked: boolean; url: string | null } = {
      revoked: false,
      url: null,
    };
    void (async () => {
      try {
        const blob = await kycAdminApi.documentFile(
          accessToken,
          doc.id,
          controller.signal,
        );
        revokedRef.url = URL.createObjectURL(blob);
        if (!revokedRef.revoked) {setPreviewUrl(revokedRef.url);}
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        const msg = e instanceof ApiError ? e.problem.detail : "Failed to load document preview.";
        setPreviewError(msg);
      }
    })();
    return () => {
      revokedRef.revoked = true;
      controller.abort();
      if (revokedRef.url !== null) {URL.revokeObjectURL(revokedRef.url);}
    };
  }, [accessToken, doc.id]);

  const isImage = doc.mime_type.startsWith("image/");
  const ocrEntries = useMemo(
    () => Object.entries(doc.ocr_extracted_data ?? {}),
    [doc.ocr_extracted_data],
  );

  const startDecision = (decision: KycAdminDecision) => {
    setPendingDecision(decision);
    setNotes("");
    setSubmitError(null);
  };
  const cancelDecision = () => {
    setPendingDecision(null);
    setNotes("");
    setSubmitError(null);
  };

  const submit = async () => {
    if (accessToken === null || pendingDecision === null) {return;}
    const trimmedNotes = notes.trim();
    if (
      (pendingDecision === "rejected" ||
        pendingDecision === "resubmit_requested") &&
      trimmedNotes.length === 0
    ) {
      setSubmitError("Notes are required for reject / resubmit decisions.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await kycAdminApi.decide(accessToken, userId, {
        document_id: doc.id,
        decision: pendingDecision,
        ...(trimmedNotes.length > 0 ? { notes: trimmedNotes } : {}),
      });
      onDecisionApplied(
        doc.id,
        res.decision,
        trimmedNotes.length > 0 ? trimmedNotes : null,
        res.user_kyc_approved,
      );
      setPendingDecision(null);
      setNotes("");
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Unexpected error. Try again.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base capitalize">
              {doc.document_type.replace(/_/g, " ")}
            </CardTitle>
            <CardDescription>
              Uploaded {formatDateTime(doc.created_at)} · {formatBytes(doc.file_size_bytes)} ·{" "}
              {doc.mime_type}
            </CardDescription>
          </div>
          <StatusPill status={doc.admin_review_status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-md border bg-muted/40 p-2">
            {previewError !== null ? (
              <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                {previewError}
              </div>
            ) : previewUrl === null ? (
              <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading
                preview…
              </div>
            ) : isImage ? (
              <a href={previewUrl} target="_blank" rel="noreferrer">
                <img
                  src={previewUrl}
                  alt={doc.document_type}
                  className="max-h-96 w-full rounded object-contain"
                />
              </a>
            ) : (
              <div className="flex h-48 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
                <span>{doc.mime_type} — not previewable inline.</span>
                <a
                  className="text-primary underline"
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open file in new tab
                </a>
              </div>
            )}
          </div>

          <div className="space-y-3 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Document number
              </div>
              <div className="font-mono">
                {doc.document_number_masked ?? "—"}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                API verification
              </div>
              <div>{API_STATUS_LABEL[doc.api_verification_status]}</div>
            </div>
            {ocrEntries.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  OCR extracted
                </div>
                <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  {ocrEntries.map(([k, v]) => (
                    <div key={k} className="contents">
                      <dt className="text-muted-foreground">{k}</dt>
                      <dd className="break-all">
                        {v === null || v === undefined
                          ? "—"
                          : typeof v === "string" ||
                              typeof v === "number" ||
                              typeof v === "boolean"
                            ? String(v)
                            : JSON.stringify(v)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {doc.admin_review_notes !== null &&
              doc.admin_review_notes.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Reviewer notes
                  </div>
                  <div className="whitespace-pre-wrap rounded border bg-background p-2 text-sm">
                    {doc.admin_review_notes}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Reviewed {formatDateTime(doc.admin_reviewed_at)}
                  </div>
                </div>
              )}
          </div>
        </div>

        {pendingDecision === null ? (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="default"
              onClick={() => { startDecision("approved"); }}
            >
              <Check className="mr-1 h-4 w-4" /> Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => { startDecision("rejected"); }}
            >
              <XCircle className="mr-1 h-4 w-4" /> Reject
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { startDecision("resubmit_requested"); }}
            >
              <RefreshCw className="mr-1 h-4 w-4" /> Request resubmit
            </Button>
          </div>
        ) : (
          <div className="space-y-2 rounded-md border bg-background p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium capitalize">
                Confirm: {pendingDecision.replace(/_/g, " ")}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelDecision}
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
            <div>
              <Label htmlFor={`notes-${doc.id}`} className="text-xs">
                Notes{" "}
                {pendingDecision === "approved"
                  ? "(optional)"
                  : "(required — the user sees this)"}
              </Label>
              <Textarea
                id={`notes-${doc.id}`}
                value={notes}
                onChange={(e) => { setNotes(e.target.value); }}
                placeholder={
                  pendingDecision === "approved"
                    ? "Optional internal note"
                    : "Tell the user what to fix or upload"
                }
                maxLength={1000}
                disabled={submitting}
              />
            </div>
            {submitError !== null && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant={
                  pendingDecision === "rejected" ? "destructive" : "default"
                }
                onClick={() => void submit()}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  "Confirm"
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function KycUserDetailPage(): React.JSX.Element {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [detail, setDetail] = useState<KycAdminUserDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userVerifiedFlash, setUserVerifiedFlash] = useState<boolean>(false);

  useEffect(() => {
    if (accessToken === null || userId === undefined) {return;}
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await kycAdminApi.userDetail(
          accessToken,
          userId,
          controller.signal,
        );
        setDetail(res);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        const msg = e instanceof ApiError ? e.problem.detail : "Unexpected error. Try again.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
    return () => { controller.abort(); };
  }, [accessToken, userId]);

  const onDecisionApplied = useCallback(
    (
      docId: string,
      newStatus: AdminReviewStatus,
      newNotes: string | null,
      userVerifiedNow: boolean,
    ) => {
      setDetail((prev) => {
        if (prev === null) {return prev;}
        const now = new Date().toISOString();
        return {
          ...prev,
          kyc_tier: userVerifiedNow ? "document_verified" : prev.kyc_tier,
          documents: prev.documents.map((d) =>
            d.id === docId
              ? {
                  ...d,
                  admin_review_status: newStatus,
                  admin_review_notes: newNotes,
                  admin_reviewed_at: now,
                }
              : d,
          ),
        };
      });
      if (userVerifiedNow) {setUserVerifiedFlash(true);}
    },
    [],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="KYC review"
        description="Review the user's identity + business profile + documents, then approve, reject, or request resubmission."
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { navigate("/kyc/queue"); }}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Queue
          </Button>
        }
      />
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && detail === null ? (
          <div className="space-y-4">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : detail !== null ? (
          <>
            {userVerifiedFlash && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>User KYC verified</AlertTitle>
                <AlertDescription>
                  Last required document approved — user is now at{" "}
                  <span className="font-mono">document_verified</span>.
                </AlertDescription>
              </Alert>
            )}

            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <CardTitle>{detail.full_name}</CardTitle>
                    <CardDescription>
                      <span className="capitalize">{detail.primary_role}</span> ·{" "}
                      <span className="font-mono">{detail.phone}</span>
                      {detail.email !== null && (
                        <>
                          {" "}
                          · <span>{detail.email}</span>
                        </>
                      )}
                    </CardDescription>
                  </div>
                  <div className="text-right text-sm">
                    <div>
                      Tier:{" "}
                      <span className="font-mono">{detail.kyc_tier}</span>
                    </div>
                    <div className="text-muted-foreground">
                      Submitted {formatDateTime(detail.kyc_submitted_at)}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-x-6 gap-y-1 text-sm md:grid-cols-2">
                  {detail.district !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">District</dt>
                      <dd>{detail.district}</dd>
                    </div>
                  )}
                  {detail.state !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">State</dt>
                      <dd>{detail.state}</dd>
                    </div>
                  )}
                  {detail.address_pincode !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">Pincode</dt>
                      <dd className="font-mono">{detail.address_pincode}</dd>
                    </div>
                  )}
                  {detail.business_name !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">Business</dt>
                      <dd>{detail.business_name}</dd>
                    </div>
                  )}
                  {detail.gstin !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">GSTIN</dt>
                      <dd className="font-mono">{detail.gstin}</dd>
                    </div>
                  )}
                  {detail.pan !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">PAN</dt>
                      <dd className="font-mono">{detail.pan}</dd>
                    </div>
                  )}
                  {detail.entity_type !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">Entity type</dt>
                      <dd>{detail.entity_type}</dd>
                    </div>
                  )}
                  {detail.primary_business_district !== null && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">
                        Business district
                      </dt>
                      <dd>{detail.primary_business_district}</dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>

            {detail.documents.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No KYC documents on file for this user.
                </CardContent>
              </Card>
            ) : (
              detail.documents.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  userId={detail.id}
                  onDecisionApplied={onDecisionApplied}
                />
              ))
            )}
          </>
        ) : null}
    </div>
  );
}
